/**
 * DeleteModelDialog: type the slug, then the model and its gateway route
 * go. Aliases block the delete, as they do in the API: a client still
 * sending one would 404 the moment the primary route disappeared.
 *
 * Owns the delete mutation, so the customer's model page and the staff
 * team configuration page share one confirm flow and differ only in the
 * scope they pass. The mutation's cache invalidation is a hook-level
 * callback, so it lands even when the caller unmounts this dialog on
 * `onDeleted`.
 */

import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useDeleteModel, type Model, type ModelScope } from '@/hooks/useModels'

interface DeleteModelDialogProps {
  model: Model
  scope: ModelScope
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fires once the model is gone; the dialog has already closed itself. */
  onDeleted: () => void
}

export function DeleteModelDialog({
  model,
  scope,
  open,
  onOpenChange,
  onDeleted,
}: DeleteModelDialogProps): React.JSX.Element {
  const deleteMutation = useDeleteModel(scope)
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(next: boolean): void {
    onOpenChange(next)
    if (!next) {
      setConfirmText('')
      setError(null)
    }
  }

  async function handleConfirm(): Promise<void> {
    try {
      await deleteMutation.mutateAsync({ modelId: model.id })
    } catch (caught) {
      setError(getApiErrorDetail(caught) ?? 'The model was not deleted')
      return
    }
    handleOpenChange(false)
    onDeleted()
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Delete model"
      description={
        <>
          This will permanently delete <strong>{model.display_name}</strong> and remove its
          route from the gateway. API calls to it will start failing immediately. This action
          cannot be undone.
        </>
      }
      confirmLabel="Delete"
      pendingLabel="Deleting..."
      isPending={deleteMutation.isPending}
      destructive
      confirmDisabled={confirmText !== model.slug || model.aliases.length > 0}
      error={error}
      onConfirm={() => {
        void handleConfirm()
      }}
    >
      {model.aliases.length > 0 && (
        <p className="text-sm text-destructive">
          Remove these aliases first: {model.aliases.join(', ')}
        </p>
      )}
      <div className="space-y-2">
        <Label htmlFor="confirm-delete">
          Type <span className="font-mono font-semibold">{model.slug}</span> to confirm
        </Label>
        <Input
          id="confirm-delete"
          value={confirmText}
          onChange={(e) => {
            setConfirmText(e.target.value)
          }}
          placeholder={model.slug}
          autoComplete="off"
          disabled={deleteMutation.isPending}
        />
      </div>
    </ConfirmDialog>
  )
}
